import React, { useState } from "react";
import { Modal, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAppDispatch } from "../../../../store";
import { updateCandidateInterviewConfiguration } from "../../../../store/interviewHostSlice";
import styles from "./candidates.module.scss";

interface CandidateInterviewConfig {
  duration_minutes: number;
  language: string;
  mandatory_questions: number;
  focus_areas: string[];
  off_limit_topics: string[];
}

interface InterviewConfigModalProps {
  open: boolean;
  onClose: () => void;
  candidateId: string;
  jobId: string;
  currentConfig: CandidateInterviewConfig;
  currentContext: string;
}

const InterviewConfigModal: React.FC<InterviewConfigModalProps> = ({
  open,
  onClose,
  candidateId,
  jobId,
  currentConfig,
  currentContext,
}) => {
  const dispatch = useAppDispatch();
  const [interviewContext, setInterviewContext] = useState(currentContext);
  const [config, setConfig] = useState({
    duration_minutes: currentConfig.duration_minutes,
    language: currentConfig.language,
    mandatory_questions: currentConfig.mandatory_questions,
    focus_areas: [...currentConfig.focus_areas],
    off_limit_topics: [...currentConfig.off_limit_topics],
  });
  const [saving, setSaving] = useState(false);
  const [newFocusArea, setNewFocusArea] = useState("");
  const [newOffLimitTopic, setNewOffLimitTopic] = useState("");

  const handleAddFocusArea = () => {
    if (newFocusArea.trim()) {
      setConfig((prev) => ({
        ...prev,
        focus_areas: [...prev.focus_areas, newFocusArea.trim()],
      }));
      setNewFocusArea("");
    }
  };

  const handleRemoveFocusArea = (index: number) => {
    setConfig((prev) => ({
      ...prev,
      focus_areas: prev.focus_areas.filter((_, i) => i !== index),
    }));
  };

  const handleAddOffLimitTopic = () => {
    if (newOffLimitTopic.trim()) {
      setConfig((prev) => ({
        ...prev,
        off_limit_topics: [...prev.off_limit_topics, newOffLimitTopic.trim()],
      }));
      setNewOffLimitTopic("");
    }
  };

  const handleRemoveOffLimitTopic = (index: number) => {
    setConfig((prev) => ({
      ...prev,
      off_limit_topics: prev.off_limit_topics.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await dispatch(
        updateCandidateInterviewConfiguration({
          candidateId,
          jobId,
          interviewContext,
          interviewConfiguration: config,
        })
      ).unwrap();
      onClose();
    } catch (error) {
      console.error("Failed to update interview configuration:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Configure Interview</h2>
          <button className={styles.modalClose} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.modalBody}>
          <label className={styles.formLabel}>
            Interview Context
            <textarea
              className={styles.formTextarea}
              value={interviewContext}
              onChange={(e) => setInterviewContext(e.target.value)}
              rows={3}
              placeholder="Add specific context for this interview..."
            />
          </label>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>
              Duration (minutes)
              <input
                type="number"
                className={styles.formInput}
                value={config.duration_minutes}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    duration_minutes: Math.min(30, Math.max(1, Number(e.target.value))),
                  })
                }
                min={1}
                max={30}
              />
              <span className={styles.formHint}>Max: 30 minutes</span>
            </label>

            <label className={styles.formLabel}>
              Language
              <select
                className={styles.formSelect}
                value={config.language}
                onChange={(e) => setConfig({ ...config, language: e.target.value })}
              >
                <option value="English">English</option>
              </select>
            </label>
          </div>

          <label className={styles.formLabel}>
            Mandatory Questions
            <input
              type="number"
              className={styles.formInput}
              value={config.mandatory_questions}
              onChange={(e) =>
                setConfig({
                  ...config,
                  mandatory_questions: Math.min(10, Math.max(1, Number(e.target.value))),
                })
              }
              min={1}
              max={10}
            />
            <span className={styles.formHint}>Max: 10 questions</span>
          </label>

          <label className={styles.formLabel}>
            Focus Areas
            <div className={styles.focusAreasList}>
              {config.focus_areas.map((area, index) => (
                <div key={index} className={styles.focusAreaItem}>
                  <span>{area}</span>
                  <button
                    type="button"
                    className={styles.btnIconSmall}
                    onClick={() => handleRemoveFocusArea(index)}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              ))}
            </div>
            <div className={styles.focusAreaInput}>
              <input
                type="text"
                className={styles.formInput}
                value={newFocusArea}
                onChange={(e) => setNewFocusArea(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddFocusArea();
                  }
                }}
                placeholder="Add focus area..."
              />
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={handleAddFocusArea}
              >
                <AddIcon sx={{ fontSize: 16 }} />
                Add
              </button>
            </div>
          </label>

          <label className={styles.formLabel}>
            Off-Limit Topics
            <div className={styles.focusAreasList}>
              {config.off_limit_topics.map((topic, index) => (
                <div key={index} className={styles.focusAreaItem}>
                  <span>{topic}</span>
                  <button
                    type="button"
                    className={styles.btnIconSmall}
                    onClick={() => handleRemoveOffLimitTopic(index)}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              ))}
            </div>
            <div className={styles.focusAreaInput}>
              <input
                type="text"
                className={styles.formInput}
                value={newOffLimitTopic}
                onChange={(e) => setNewOffLimitTopic(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddOffLimitTopic();
                  }
                }}
                placeholder="Add off-limit topic..."
              />
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={handleAddOffLimitTopic}
              >
                <AddIcon sx={{ fontSize: 16 }} />
                Add
              </button>
            </div>
          </label>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleSave}
            disabled={saving || config.focus_areas.length === 0}
          >
            {saving ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default InterviewConfigModal;
